import { useEffect, useState } from "react"
import { useIsOpen } from "@oefr/hooks"
import { type JourneyType } from "@/scalars/journeyType"
import { EnergyType } from "../../types/energyType"
import { type AddressLookupEligibility } from "../types/addressLookupEligibility"

type UseStateEligibilityFormsProps = {
    isElectricityMeterPointEligible: boolean
    isGasMeterPointEligible: boolean
    journeyType: JourneyType
    energyTypesAvalaible: EnergyType[]
}

export function useStateEligibilityForm({
    isElectricityMeterPointEligible,
    isGasMeterPointEligible,
    journeyType,
    energyTypesAvalaible,
}: UseStateEligibilityFormsProps) {
    const hasElectricity = energyTypesAvalaible.includes(EnergyType.Electricity)
    const hasGas = energyTypesAvalaible.includes(EnergyType.Gas)

    const meterPointIdForms = {
        [EnergyType.Electricity]: useIsOpen(!isElectricityMeterPointEligible),
        [EnergyType.Gas]: useIsOpen(
            (!hasElectricity || isElectricityMeterPointEligible) && !isGasMeterPointEligible
        ),
    }

    const electricityMeterPointForm = {
        isFormOpen: meterPointIdForms.electricity.isOpen,
        open: () => {
            meterPointIdForms.electricity.open()
        },
        close: () => {
            meterPointIdForms.electricity.close()
            setAddressLookupFailed((previousData) => ({
                ...previousData,
                [EnergyType.Electricity]: false,
            }))

            if (!isGasMeterPointEligible) meterPointIdForms.gas.open()
        },
    }

    const gasMeterPointForm = {
        isFormOpen: meterPointIdForms.gas.isOpen,
        open: () => {
            meterPointIdForms.gas.open()
        },
        close: () => {
            meterPointIdForms.gas.close()
            setAddressLookupFailed((previousData) => ({
                ...previousData,
                [EnergyType.Gas]: false,
            }))
        },
    }

    const addressLookupForm = useIsOpen(
        !isElectricityMeterPointEligible && !isGasMeterPointEligible
    )
    const [addressLookupFailed, setAddressLookupFailed] = useState({
        [EnergyType.Electricity]: false,
        [EnergyType.Gas]: false,
    })

    const handleAddressLookupFormSubmission = (eligibilities: AddressLookupEligibility[]) => {
        const getEnergyEligibility = (energyType: EnergyType) =>
            !!eligibilities.find((eligibility) => eligibility.energyType === energyType)?.isEligible

        const isElectricityEligible = getEnergyEligibility(EnergyType.Electricity)
        const isGasEligible = getEnergyEligibility(EnergyType.Gas)

        if (isElectricityEligible) {
            meterPointIdForms.electricity.close()

            if (hasGas && !isGasEligible) {
                meterPointIdForms.gas.open()
            }
        } else {
            if (hasElectricity) {
                meterPointIdForms.electricity.open()
                meterPointIdForms.gas.close()
            } else {
                if (hasGas && !isGasEligible) {
                    meterPointIdForms.gas.open()
                }
            }
        }

        setAddressLookupFailed({
            [EnergyType.Electricity]: hasElectricity && !isElectricityEligible,
            [EnergyType.Gas]: hasGas && !isGasEligible,
        })

        addressLookupForm.close()
    }

    useEffect(() => {
        meterPointIdForms.electricity.open()
        meterPointIdForms.gas.open()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [journeyType])

    return {
        addressLookupForm: {
            isFormOpen: addressLookupForm.isOpen,
            open: addressLookupForm.open,
            close: addressLookupForm.close,
            onSubmit: handleAddressLookupFormSubmission,
            failed: addressLookupFailed,
        },
        meterPointIdForms: {
            electricity: electricityMeterPointForm,
            gas: gasMeterPointForm,
        },
    }
}
